import {Avatar, Box, Button, Grid, IconButton, Stack, Toolbar, Tooltip, Typography} from "@mui/material";
import React, {useContext, useEffect, useMemo, useState} from "react";
import {
  IconBug,
  IconChevronLeft,
  IconX
} from "@tabler/icons-react";
import moment from "moment";
import {green, grey, lightBlue} from "@mui/material/colors";
import {ConversationRepository} from "../../../../repositories/ConversationRepository";
import {toast} from "react-toastify";
import {SignInContext} from "../../../../context/SignInContext";
import useConfirm from "../../../../hooks/useConfirm";
import {LocalPhone, PhoneForwarded} from "@mui/icons-material";
import {SupervisionConversationHistory} from "./supervision-conversation-history";
import {useAtom} from "jotai";
import {SelectedConversationAudioPlayer, SelectedMessageAudioPlayer} from "../atoms";
import {formatTime} from "../../../../utils/functions";
import {ToggleResolvedButton} from "./toggle-resolve-button";
import {useNavigate, useParams} from "react-router-dom";
import LoadingScreen from "../../../../components/loading-screen/LoadingScreen";
import {ContactPhoneNumber} from "./contact-phone-number";
import {useStompWebsocket} from "../../../../hooks/useStompWebsocket";
import {FlowProtectionButton} from "../../../../components/FlowProtectionButton";


export const SupervisionConversation = ({project}) => {
  const {conversationId} = useParams();
  const [updatedValue] = useStompWebsocket(conversationId ? `/topic/session/${conversationId}` : null);
  const navigate = useNavigate();
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [selectedAudioConversation, setSelectedAudioConversation] = useAtom(SelectedConversationAudioPlayer);
  const [selectedAudioMessage, setSelectedAudioMessage] = useAtom(SelectedMessageAudioPlayer);
  const [showDebug, setShowDebug] = useState(false);
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState(null);
  const {currentUser} = useContext(SignInContext);
  const confirmMonitoring = useConfirm(
    "This conversation is already being monitored by " + selectedConversation?.monitoredBy || "someone",
    "Are you sure you want to take over monitoring from " + selectedConversation?.monitoredBy || "someone" + "?"
  );
  const confirm = useConfirm(
    'Do you want to initiate conversation',
  );

  useEffect(() => {
    if (conversationId) {
      ConversationRepository.getConversation({id: conversationId})
        .then(response => setSelectedConversation(response?.data))
        .catch(() => navigate(`/project/${project?.id}/supervision`))
    }
  }, [conversationId, updatedValue]);

  useEffect(() => {
    const username = currentUser?.username;
    const hasVdn = !!(project?.vdnManager ? project?.vdnManager[username] || undefined : undefined);
    if (selectedConversation?.id && !selectedConversation?.monitoredBy && hasVdn && selectedConversation?.type !== "CHAT_BOT") {
      monitorConversation()
        .then(() => console.log("set monitor conversation"))
        .catch(() => console.log("failed to monitor conversation"))
    }
    const debug = localStorage.getItem("showDebug");
    setShowDebug(debug ? JSON.parse(debug) || false : false);
  }, [selectedConversation?.id, project, currentUser]);

  const handleHandover = () => {
    const vdnManager = project?.vdnManager || {};
    let vdn = project?.configuration?.redirect_vdn;
    if (vdnManager[currentUser?.username]) {
      vdn = vdnManager[currentUser?.username];
    }
    ConversationRepository.handoverToAgent({id: selectedConversation?.id, username: currentUser?.username, vdn: vdn})
      .then(response => {
        setSelectedConversation(prev => response?.data || prev);
        toast.success("Successfully handover to agent")
      })
      .catch(() => {
        toast.error("Failed to handover to agent")
      })
  }
  const monitorConversation = async () => {
    if (selectedConversation?.monitoredBy && selectedConversation?.monitoredBy !== currentUser?.username) {
      const confirmed = await confirmMonitoring();

      if (!confirmed) return;
    }
    ConversationRepository.monitorConversation({id: selectedConversation?.id, username: currentUser?.username})
      .then(response => {
        setSelectedConversation(prev => response?.data || prev);
        toast.success("You are now monitoring conversation")
      })
      .catch(() => {
        toast.error("Failed to monitor conversation")
      })
  }

  const initiateConversation = async (e) => {
    e?.stopPropagation();
    e?.preventDefault();

    let agentVdn = project?.configuration?.redirect_vdn;
    if (project?.vdnManager[currentUser?.username]) {
      agentVdn = project?.vdnManager[currentUser?.username];
    }
    if (agentVdn && selectedConversation?.sender) {
      const body = {
        username: currentUser?.username,
        vdn: agentVdn,
        phone_number: selectedConversation?.sender,
        initiate_call_api: project?.configuration?.initiate_call_api
      };
      const params = {conversation_id: selectedConversation?.id, flow_uid: project?.id};

      const response = await confirm({title: `Do you want to initiate conversation with ${body?.phone_number}`})
      if (response) {
        ConversationRepository.initiateConversation({body, params})
          .then(response => {
            toast.success(response?.data?.message || `Initiated conversation with ${body?.phone_number}`);
          })
          .catch(error => {
            toast.error(error?.message || `Failed to initiated conversation with ${body?.phone_number}`);
          })
      }
    }
  }

  const onClose = () => {
    navigate(`/project/${project?.id}/supervision`)
    setSelectedConversation(null);
    setSelectedAudioMessage(null);
    setSelectedAudioConversation(null);
  }

  const toggleResolved = () => {
    ConversationRepository.toggleResolved({id: selectedConversation?.id})
      .then(response => setSelectedConversation(prev => response?.data || prev))
      .catch(error => console.log(error))
  }

  const contact = useMemo(() => {
    return project?.phonebook?.[selectedConversation?.sender] || null;
  }, [project?.phonebook?.[selectedConversation?.sender], selectedConversation?.sender])

  if (selectedConversation === null) {
    return <LoadingScreen />
  }

  return (
    <>
      <ContactPhoneNumber
        project={project} phoneNumber={selectedPhoneNumber}
        open={selectedPhoneNumber !== null}
        handleClose={() => setSelectedPhoneNumber(null)}
      />
      <Toolbar sx={{px: 0, justifyContent: "space-between"}}>
        <Stack direction={"row"} gap={1}>
          <Box><IconButton onClick={onClose}><IconChevronLeft/></IconButton></Box>
          <Box>
            <Tooltip title={`${selectedConversation?.sender} - ${contact || "Add as contact"}`}>
              <Typography sx={{cursor: "pointer"}} fontWeight={500} onClick={() => setSelectedPhoneNumber(selectedConversation?.sender)}>
                {selectedConversation?.id}. {contact || selectedConversation?.sender}
              </Typography>
            </Tooltip>
            <Typography fontWeight={400} color={grey[500]}
                        fontSize={"0.8em"}>
              {moment(selectedConversation?.dateCreated)?.format("DD MMM YYYY HH:mm")}
            </Typography>
          </Box>
        </Stack>
        {selectedConversation?.type !== "CHAT_BOT" &&
          <Typography fontWeight={500} fontSize={"0.9em"} color={grey[800]}>
            Monitored by: <em style={{fontWeight: 400}}>{selectedConversation?.monitoredBy || "Not monitored"}</em>
          </Typography>
        }

        <Stack direction={"row"} alignItems={"center"} gap={1}>

          {
            (selectedAudioConversation?.id || selectedAudioMessage?.id) &&
            <Stack direction={"row"} gap={1} alignItems={"center"} className={"border-shadow"}
                   sx={{pl: 2, py: 0, pr: 0, borderRadius: 3}}>
              <Typography>{formatTime(selectedAudioConversation?.currentTime || selectedAudioMessage?.currentTime || 0)} / <strong
                style={{fontWeight: 500}}>{formatTime(selectedAudioConversation?.duration || selectedAudioMessage?.duration || 0)}</strong></Typography>
              <IconButton
                onClick={() => {
                  setSelectedAudioConversation(null);
                  setSelectedAudioMessage(null);
                }}
                size={"small"}
              >
                <IconX size={18}/>
              </IconButton>
            </Stack>
          }
          {selectedConversation?.type !== "CHAT_BOT" &&
            <>
              {
                selectedConversation?.status === "ACTIVE" ?
                  <>
                    <Box>
                      <Button color={"info"} size={"small"} onClick={handleHandover}
                              sx={{bgcolor: lightBlue[50], px: 2, borderRadius: 3}}
                              startIcon={<PhoneForwarded fontSize={"small"}/>}>
                        Handover
                      </Button>
                    </Box>
                  </> :
                  <>
                    <ToggleResolvedButton conversation={selectedConversation} toggleResolved={toggleResolved} />
                    <Box>
                      <Button color={"success"} size={"small"} onClick={initiateConversation}
                              sx={{bgcolor: green[50], px: 2, borderRadius: 4}}
                              startIcon={<LocalPhone fontSize={"small"}/>}>
                        Call Customer
                      </Button>
                    </Box>
                  </>
              }
            </>
          }
          <Box>
            <Tooltip title={"Show Debug Messages"}>
              <IconButton
                size={"small"}
                color={showDebug ? "success" : "default"}
                onClick={() => {
                  setShowDebug(prev => {
                    localStorage.setItem("showDebug", JSON.stringify(!prev))
                    return !prev;
                  })
                }}
              >
                <IconBug size={22}/>
              </IconButton>
            </Tooltip>
          </Box>
          <FlowProtectionButton flowUid={selectedConversation?.flowUid || project?.id}/>
        </Stack>
      </Toolbar>
      <Grid container alignItems={"stretch"} sx={{flex: 1}}>
        <Grid item xs={12} md={12} lg={12}
              sx={{maxHeight: "calc(100dvh - 50px - 64px)", overflowY: "auto", position: "relative"}}>
          <SupervisionConversationHistory selectedConversation={selectedConversation}
                                          setSelectedConversation={setSelectedConversation}
                                          debug={showDebug} project={project} />
        </Grid>
      </Grid>

    </>
  )
}